import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Box, 
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import CloseButton from './Common/CloseButton';

/**
 * CaseActionDialog Component
 * 
 * A reusable dialog component for case-related actions like renaming and duplicating.
 * This component provides a consistent UI for text input operations on cases.
 * 
 * Features:
 * - Supports both rename and copy/duplicate operations
 * - Consistent styling with gradient header and modern design
 * - Keyboard support (Enter to submit)
 * - Internationalization support
 * - Customizable colors and text
 */
const CaseActionDialog = ({ 
  open, 
  onClose, 
  value, 
  onValueChange, 
  onSubmit,
  type = 'rename', // 'rename' or 'copy'
  primaryColor = '#5757b0',
  hoverColor = '#7777d0',
  title = null,
  placeholder = null,
  submitButtonText = null,
  extraContent = null
}) => {
  const { t } = useTranslation();

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      onSubmit();
    }
  };

  const getIcon = () => {
    return type === 'copy' ? <ContentCopyIcon sx={{ mr: 1.5, fontSize: '1.4rem' }} /> : <EditIcon sx={{ mr: 1.5, fontSize: '1.4rem' }} />;
  };

  const getTitle = () => {
    if (title) return title;
    return type === 'copy' ? (t('sidebar.copy_case') || 'Duplicate Case') : (t('sidebar.rename_case') || 'Rename Case');
  };

  const getPlaceholder = () => {
    if (placeholder) return placeholder;
    return t('sidebar.case_name') || 'Case Name';
  };

  const getSubmitButtonText = () => {
    if (submitButtonText) return submitButtonText;
    return type === 'copy' ? (t('sidebar.copy_case') || 'Duplicate') : (t('sidebar.rename_case') || 'Rename');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 12px 40px rgba(87, 87, 176, 0.2)',
          overflow: 'hidden',
          position: 'relative',
          minHeight: '320px',
          maxHeight: '400px',
        }
      }}
    >
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${primaryColor}, ${hoverColor})`,
        color: 'white',
        fontWeight: '600',
        fontSize: '1.25rem',
        py: 2.5,
        px: 3,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '70px',
        boxSizing: 'border-box',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {getIcon()}
          {getTitle()}
        </Box>
        <CloseButton
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
            p: 0.8,
          }}
        />
      </DialogTitle>
      
      <DialogContent sx={{ 
        pt: 0, 
        pb: 0, 
        px: 4,
        mt: 0,
        minHeight: '120px',
        background: 'linear-gradient(180deg, rgba(247, 247, 255, 0.8) 0%, rgba(255, 255, 255, 1) 100%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          label={getPlaceholder()}
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          onKeyPress={handleKeyPress}
          sx={{
            mt: 2,
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              backgroundColor: 'white',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              minHeight: '56px',
              '& fieldset': {
                borderColor: 'rgba(87, 87, 176, 0.2)',
                borderWidth: '1.5px',
              },
              '&:hover fieldset': {
                borderColor: hoverColor,
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderColor: primaryColor,
                borderWidth: '2px',
                boxShadow: `0 0 0 3px rgba(87, 87, 176, 0.1)`,
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(87, 87, 176, 0.7)',
              fontWeight: '500',
              '&.Mui-focused': {
                color: primaryColor,
              },
            },
            '& .MuiOutlinedInput-input': {
              py: 2,
              px: 2,
              fontSize: '1rem',
            },
          }}
        />
        {extraContent}
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 4, 
        pt: 3,
        gap: 2,
        background: 'rgba(247, 247, 255, 0.3)',
        borderTop: '1px solid rgba(87, 87, 176, 0.1)',
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ 
            color: 'rgba(87, 87, 176, 0.8)',
            borderColor: 'rgba(87, 87, 176, 0.3)',
            borderRadius: '8px',
            px: 3,
            py: 1,
            fontWeight: '500',
            textTransform: 'none',
            '&:hover': { 
              backgroundColor: 'rgba(87, 87, 176, 0.05)',
              borderColor: 'rgba(87, 87, 176, 0.5)',
            }
          }}
        >
          {t('close') || 'Cancel'}
        </Button>
        <Button 
          onClick={onSubmit}
          variant="contained"
          sx={{
            backgroundColor: primaryColor,
            borderRadius: '8px',
            px: 3,
            py: 1,
            fontWeight: '600',
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(87, 87, 176, 0.4)',
            '&:hover': { 
              backgroundColor: hoverColor,
              boxShadow: '0 6px 16px rgba(87, 87, 176, 0.5)',
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {getSubmitButtonText()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CaseActionDialog;
